import React, { useState } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Snackbar,
  TextField,
  Typography,
} from '@mui/material';
import { useAuthStore } from '@/stores/authStore';
import { upgradeWechatTenant, joinExistingTenant } from '@/api/auth';

/**
 * 微信扫码报名占位账号的全局引导横幅（非阻塞）。
 * 当当前登录用户是微信占位账号（isWechatPlaceholder=true）时，在内容区顶部展示提示，
 * 用户点击「完善企业信息」后弹出表单：默认原地升级为正式企业；
 * 若提交的手机号/邮箱命中已有真实企业账号，则二次确认「加入/切换」。
 */
const WechatUpgradeBanner: React.FC = () => {
  const user = useAuthStore((s) => s.user);
  const setToken = useAuthStore((s) => s.setToken);
  const setUser = useAuthStore((s) => s.setUser);
  const setCurrentTenant = useAuthStore((s) => s.setCurrentTenant);

  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ name: '', companyName: '', phone: '', email: '', password: '', confirm: '' });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [joinTarget, setJoinTarget] = useState<{ id: string; name: string } | null>(null);
  const [snack, setSnack] = useState('');

  // hooks 必须无条件调用；非占位账号直接不渲染
  if (!user || !user.isWechatPlaceholder) return null;

  const applySession = (data: {
    user: Parameters<typeof setUser>[0];
    tenant: { id: string; name: string };
    token: string;
    role: 'tenant_admin' | 'member';
  }) => {
    setToken(data.token);
    setUser(data.user);
    setCurrentTenant({ id: data.tenant.id, name: data.tenant.name } as any, data.role as any);
  };

  const handleSubmit = async () => {
    setError('');
    if (!form.name.trim() || !form.companyName.trim() || !form.phone.trim() || !form.email.trim() || !form.password) {
      setError('请填写完整的联系人姓名、公司名称、手机号、邮箱与登录密码');
      return;
    }
    if (!/^1\d{10}$/.test(form.phone.trim())) {
      setError('手机号格式不正确');
      return;
    }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email.trim())) {
      setError('邮箱格式不正确');
      return;
    }
    if (form.password.length < 6) {
      setError('密码至少 6 位');
      return;
    }
    if (form.password !== form.confirm) {
      setError('两次输入的密码不一致');
      return;
    }
    setLoading(true);
    try {
      const r = await upgradeWechatTenant({
        displayName: form.name.trim(),
        companyName: form.companyName.trim(),
        phone: form.phone.trim(),
        email: form.email.trim(),
        password: form.password,
      });
      const d = r.data;
      if (d.action === 'join' && d.existingTenant) {
        setJoinTarget(d.existingTenant);
        return;
      }
      if (d.action === 'upgraded' && d.token && d.user && d.tenant) {
        applySession({ user: d.user, tenant: d.tenant, token: d.token, role: 'tenant_admin' });
        setSnack('已升级为正式企业，欢迎使用！');
        setOpen(false);
        return;
      }
      setError('升级失败，请稍后重试');
    } catch (e: any) {
      setError(e?.response?.data?.message || e?.message || '提交失败，请稍后重试');
    } finally {
      setLoading(false);
    }
  };

  const handleJoin = async () => {
    if (!joinTarget) return;
    if (!form.name.trim()) {
      setError('请填写联系人姓名');
      return;
    }
    setLoading(true);
    setError('');
    try {
      const r = await joinExistingTenant({ targetTenantId: joinTarget.id, displayName: form.name.trim() });
      const d = r.data;
      applySession({ user: d.user, tenant: d.tenant, token: d.token, role: 'member' });
      setSnack('已加入「' + joinTarget.name + '」，并切换至该企业');
      setJoinTarget(null);
      setOpen(false);
    } catch (e: any) {
      setError(e?.response?.data?.message || e?.message || '加入失败，请稍后重试');
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <Alert
        severity="info"
        sx={{ mb: 2, alignItems: 'center' }}
        action={
          <Button
            color="inherit"
            size="small"
            variant="outlined"
            onClick={() => setOpen(true)}
            disabled={loading}
          >
            完善企业信息
          </Button>
        }
      >
        检测到您是通过微信扫码报名生成的临时账号，完善企业信息后即可正式使用全部功能。
      </Alert>

      {/* 升级表单 */}
      <Dialog
        open={open}
        onClose={() => !loading && setOpen(false)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>完善企业信息</DialogTitle>
        <DialogContent>
          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}
          <TextField
            label="联系人姓名"
            fullWidth
            margin="dense"
            value={form.name}
            onChange={(e) => setForm({ ...form, name: e.target.value })}
            disabled={loading}
          />
          <TextField
            label="企业名称"
            fullWidth
            margin="dense"
            value={form.companyName}
            onChange={(e) => setForm({ ...form, companyName: e.target.value })}
            disabled={loading}
          />
          <TextField
            label="手机号"
            fullWidth
            margin="dense"
            value={form.phone}
            onChange={(e) => setForm({ ...form, phone: e.target.value })}
            disabled={loading}
            inputProps={{ inputMode: 'numeric' }}
          />
          <TextField
            label="邮箱"
            fullWidth
            margin="dense"
            value={form.email}
            onChange={(e) => setForm({ ...form, email: e.target.value })}
            disabled={loading}
          />
          <TextField
            label="设置登录密码"
            type="password"
            fullWidth
            margin="dense"
            value={form.password}
            onChange={(e) => setForm({ ...form, password: e.target.value })}
            disabled={loading}
          />
          <TextField
            label="确认密码"
            type="password"
            fullWidth
            margin="dense"
            value={form.confirm}
            onChange={(e) => setForm({ ...form, confirm: e.target.value })}
            disabled={loading}
            error={!!form.confirm && form.confirm !== form.password}
            helperText={form.confirm && form.confirm !== form.password ? '两次输入不一致' : ''}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpen(false)} disabled={loading}>
            取消
          </Button>
          <Button onClick={handleSubmit} variant="contained" disabled={loading}>
            {loading ? <CircularProgress size={18} /> : '提交并升级'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* 加入/切换二次确认 */}
      <Dialog
        open={!!joinTarget}
        onClose={() => !loading && setJoinTarget(null)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>加入已有企业</DialogTitle>
        <DialogContent>
          <Typography variant="body2">
            您填写的手机号或邮箱已属于企业「{joinTarget?.name}」，是否直接加入并切换至该企业？
          </Typography>
          <TextField
            label="联系人姓名"
            fullWidth
            margin="dense"
            value={form.name}
            onChange={(e) => setForm({ ...form, name: e.target.value })}
            disabled={loading}
          />
          {error && (
            <Alert severity="error" sx={{ mt: 2 }}>
              {error}
            </Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setJoinTarget(null)} disabled={loading}>
            取消
          </Button>
          <Button onClick={handleJoin} variant="contained" disabled={loading}>
            {loading ? <CircularProgress size={18} /> : '加入并切换'}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={!!snack}
        autoHideDuration={3000}
        onClose={() => setSnack('')}
        message={snack}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      />
    </>
  );
};

export default WechatUpgradeBanner;
